import { useState } from "react";
import { api, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, X, Send } from "lucide-react";

/**
 * Owner-triggered weekly digest preview.
 * Sending is intentionally not wired yet — the site owner will pick a provider later.
 */
export default function DigestPreview() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/moderation/digest/preview");
      setDigest(data);
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not build preview.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => setDigest(null);

  const fakeSend = () => {
    toast("Sending is not wired yet — pick an email provider when you're ready and I'll turn it on.");
  };

  return (
    <section className="mt-14">
      <div className="rounded-3xl border border-[#B79256]/30 bg-white p-6">
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div>
            <div className="accent-label text-[#B79256]">weekly digest</div>
            <h2 className="font-serif-display text-3xl text-[#2A2823] mt-1">Sunday email</h2>
            <p className="text-[#2A2823]/70 mt-2 max-w-xl">
              A quiet, editorial roundup of the freshest honest branches from the past week. Sponsored content, if included, is always clearly labeled. Preview what would go out before we wire the sender.
            </p>
          </div>
          <Button
            data-testid="digest-preview-btn"
            onClick={load}
            disabled={loading}
            className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-11 px-5 shadow-none"
          >
            <Mail className="w-4 h-4 mr-2" strokeWidth={1.75}/> {loading ? "Building…" : "Preview this Sunday's email"}
          </Button>
        </div>

        <p className="mt-4 text-xs accent-label text-[#B79256]">sending not wired — email provider not chosen yet</p>
      </div>

      {digest && (
        <div className="fixed inset-0 z-50 bg-[#2A2823]/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" role="dialog" data-testid="digest-modal">
          <div className="bg-[#FBF8F1] rounded-3xl max-w-2xl w-full my-8 overflow-hidden border border-[#B79256]/30 shadow-[0_30px_80px_rgb(42,40,35,0.2)] flex flex-col max-h-[90vh]">
            {/* Modal header (outside the "email") */}
            <div className="flex items-center justify-between px-5 py-3 bg-white/60 border-b border-[#B79256]/25 flex-shrink-0">
              <div>
                <div className="accent-label text-[#B79256]">preview · not sent</div>
                <div className="text-sm text-[#2A2823]/70">
                  Would reach <b data-testid="digest-recipients">{digest.recipient_count}</b> {digest.recipient_count === 1 ? "person" : "people"} · {digest.branch_count} branch{digest.branch_count === 1 ? "" : "es"}
                </div>
              </div>
              <button
                onClick={close}
                aria-label="Close preview"
                data-testid="digest-close"
                className="p-2 rounded-full hover:bg-[#B79256]/15 text-[#2A2823]/60 hover:text-[#2A2823] transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={1.75}/>
              </button>
            </div>

            {/* Simulated email body */}
            <div className="p-6 md:p-10 overflow-y-auto flex-1">
              <div className="text-xs text-[#2A2823]/50">
                <b className="text-[#2A2823]/70">Subject:</b> <span data-testid="digest-subject">{digest.subject}</span>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <img src="/favicon-64.png" alt="Willoah" className="w-10 h-10 rounded-full border border-[#B79256]/30"/>
                <div>
                  <div className="font-serif-display text-3xl text-[#2A2823] leading-none">Willoah</div>
                  <div className="accent-label text-[#B79256] mt-1">real life. real ideas.</div>
                </div>
              </div>

              <p className="mt-6 font-serif-display text-2xl text-[#4E5A3D] leading-snug">
                A few honest branches from the past week.
              </p>
              <p className="mt-3 text-[#2A2823]/75 leading-relaxed">
                Hey — a note from the founder. Willoah is where real people share what actually works —
                without an algorithm deciding what you see.
                Pour a coffee and see what caught our attention this week.
              </p>

              <ul className="mt-8 space-y-6" data-testid="digest-branches">
                {digest.branches.map((b) => (
                  <li key={b.id} data-testid={`digest-branch-${b.id}`} className="flex gap-4">
                    <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[#B79256]/10 flex-shrink-0 border border-[#B79256]/20">
                      <img src={FILE_URL(b.photo_path)} alt={b.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <div className="accent-label text-[#B79256]">{b.category}</div>
                      <div className="font-serif-display text-xl text-[#2A2823] leading-tight">{b.title}</div>
                      <div className="text-xs text-[#2A2823]/60 mt-1">by {b.author_name}</div>
                      <div className="accent-label text-[#4E5A3D] mt-2">{b.proof_label}</div>
                    </div>
                  </li>
                ))}
                {digest.branches.length === 0 && (
                  <li className="text-[#2A2823]/60 italic">The Grove was quiet this week. Nothing to send.</li>
                )}
              </ul>

              <div className="mt-10 pt-6 border-t border-[#B79256]/25 text-sm text-[#2A2823]/60">
                <p>You're on this list because you signed in to Willoah.</p>
                <p className="mt-1">Sponsored content is always clearly labeled, and brands can never buy a positive review.</p>
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-white/60 border-t border-[#B79256]/25 flex-shrink-0">
              <p className="text-xs text-[#2A2823]/60">Sender not wired yet — this preview is safe to look at.</p>
              <div className="flex gap-2">
                <Button
                  onClick={close}
                  variant="outline"
                  className="rounded-full h-10 px-5 border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]"
                >
                  Close
                </Button>
                <Button
                  data-testid="digest-send-btn"
                  onClick={fakeSend}
                  disabled
                  className="rounded-full h-10 px-5 bg-[#4E5A3D]/50 text-[#FBF8F1] cursor-not-allowed shadow-none"
                  title="Sending will turn on when an email provider is connected"
                >
                  <Send className="w-4 h-4 mr-2" strokeWidth={1.75}/> Send (disabled)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
